import { subMonths } from "date-fns/subMonths";
import { subYears } from "date-fns/subYears";

export type ChangeType = "positive" | "negative" | "unknown";

/**
 * BIで使う月ごとのVoC件数データ(VocCount)をまとめているクラス
 */
export class VocCountData {
  constructor(
    public readonly counts: VocCount[],
    // 最新月はcountsから計算しないで値として保持する (filterしたときにおかしくなるため)
    public readonly latestYearMonth: Date,
    // どのカテゴリパスの子countsを持っているか (e.g. この値が`/a/`の場合, countsは`/a/**/*/`のリストになる)
    public readonly currentFilteringPath: string = "/",
  ) {}

  /**
   * 過去12ヶ月分のVoC件数のリスト, Indexが若い方が古い月
   */
  get stats(): number[] {
    return Array.from({ length: 12 }, (_, i) => {
      const nextYM = subMonths(this.latestYearMonth, i);
      return sumVocCountsByYearMonth(
        this.counts,
        nextYM.getFullYear(),
        nextYM.getMonth() + 1,
      );
    }).toReversed();
  }

  // 今月のVoC件数
  get thisMonthCount() {
    return this.stats.at(-1);
  }

  // 先月からの増減数
  get changeAmountFromPrevMonth() {
    const recent = this.stats.slice(-2);
    return recent[1] - recent[0];
  }

  // 先月比(%)
  get monthToMonthComparison() {
    const recent = this.stats.slice(-2);
    if (recent[0] === 0) return undefined;

    const result = Math.round((recent[1] / recent[0] - 1) * 100);
    return Object.is(result, -0) ? 0 : result;
  }

  get changeTypeFromPrevMonth(): ChangeType {
    if (this.monthToMonthComparison === undefined) return "unknown";
    return this.changeAmountFromPrevMonth < 0 ? "negative" : "positive";
  }

  /**
   * 過去5年分のVoC件数のリスト, Indexが若い方が古い年
   */
  get yearlyStats(): number[] {
    return Array.from({ length: 5 }, (_, i) => {
      const nextYear = subYears(this.latestYearMonth, i);
      return sumVocCountsByYear(
        this.counts,
        nextYear.getFullYear(),
      );
    }).toReversed();
  }

  // 今年のVoC件数
  get thisYearCount() {
    return this.yearlyStats.at(-1);
  }

  // 前年からの増減数
  get changeAmountFromPrevYear() {
    const recent = this.yearlyStats.slice(-2);
    return recent[1] - recent[0];
  }

  // 前年比(%)
  get yearToYearComparison() {
    const recent = this.yearlyStats.slice(-2);
    if (recent[0] === 0) return undefined;

    const result = Math.round((recent[1] / recent[0] - 1) * 100);
    return Object.is(result, -0) ? 0 : result;
  }

  get changeTypeFromPrevYear(): ChangeType {
    if (this.yearToYearComparison === undefined) return "unknown";
    return this.changeAmountFromPrevYear < 0 ? "negative" : "positive";
  }

  filterByCategoryName(category: string): VocCountData {
    const path = `${this.currentFilteringPath}${category}/`;

    return new VocCountData(
      this.counts.filter((vc) => vc.categoryPath.startsWith(path)),
      this.latestYearMonth,
      path,
    );
  }
}

export class VocCount {
  constructor(
    public readonly categoryPath: string,
    public readonly year: number,
    public readonly month: number,
    public readonly count: number,
  ) {}

  get date() {
    return new Date(this.year, this.month - 1, 1);
  }

  get layers() {
    return this.categoryPath.split("/").slice(1, -1);
  }

  getLayerName(level: number) {
    return this.layers[level - 1];
  }
}

export const sumVocCountsByYearMonth = (
  vocCounts: VocCount[],
  year: number,
  month: number,
) => {
  return vocCounts
    .filter((vc) => vc.year === year && vc.month === month)
    .reduce((acc, vc) => acc + vc.count, 0);
};

export const sumVocCountsByYear = (
  vocCounts: VocCount[],
  year: number,
) => {
  return vocCounts
    .filter((vc) => vc.year === year)
    .reduce((acc, vc) => acc + vc.count, 0);
};
